// Mirror of src/marine_detect/tracking.py — first/last-seen accounting per
// track id, with grace-period eviction. Pure logic, accepts an injectable clock.

import type { Detection } from "../inference/types";

export type Clock = () => number; // returns seconds

interface TrackEntry {
  trackId: number;
  clsName: string;
  firstSeen: number;
  lastSeen: number;
}

export interface TrackRegistryOptions {
  graceSeconds?: number;
  clock?: Clock;
}

export class TrackRegistry {
  graceSeconds: number;
  clock: Clock;
  private tracks: Map<number, TrackEntry> = new Map();

  constructor(opts: TrackRegistryOptions = {}) {
    this.graceSeconds = opts.graceSeconds ?? 2.0;
    this.clock = opts.clock ?? (() => performance.now() / 1000);
  }

  /** Record this frame's detections; return them with `seen_for_seconds` set. */
  update(detections: Detection[]): Detection[] {
    const now = this.clock();
    const seenNow = new Set<number>();
    const out: Detection[] = [];

    for (const det of detections) {
      if (det.track_id === null) {
        out.push(det);
        continue;
      }
      seenNow.add(det.track_id);
      let entry = this.tracks.get(det.track_id);
      if (!entry) {
        entry = {
          trackId: det.track_id,
          clsName: det.cls_name,
          firstSeen: now,
          lastSeen: now,
        };
        this.tracks.set(det.track_id, entry);
      } else {
        entry.lastSeen = now;
        entry.clsName = det.cls_name;
      }
      out.push({ ...det, seen_for_seconds: now - entry.firstSeen });
    }

    // Evict stale tracks
    for (const [tid, e] of this.tracks) {
      if (!seenNow.has(tid) && now - e.lastSeen > this.graceSeconds) {
        this.tracks.delete(tid);
      }
    }
    return out;
  }

  get activeCount(): number {
    return this.tracks.size;
  }

  get longestSeconds(): number {
    if (this.tracks.size === 0) return 0;
    const now = this.clock();
    let max = 0;
    for (const e of this.tracks.values()) {
      const s = now - e.firstSeen;
      if (s > max) max = s;
    }
    return max;
  }
}
